import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
  Grid,
  Container,
  Divider,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { CircularProgress } from '@material-ui/core';
import { fetchObjectivesInfo } from '../../api/objective/objectivesInfo.api';
import { addAlert } from '../../actions/alerts.action';
import { createNext2WeeksValue, convertDateToModernCalendarFormat } from '../../utils/dates';
import { STATUS, DEVELOPMENT_STATUS, OBJECTIVES_INFO_TYPES } from '../../utils/const';
import { useFilterContext } from '../Filters/FilterProvider';

const useStyles = makeStyles({
  container: {
    backgroundColor: '#F5F5F5',
    padding: '20px'
  },
  number: {
    fontSize: '40px',
    fontWeight: '700'
  },
  clickable: {
    textAlign: 'center',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  willExpire: {
    color: '#F48401',
    '&:hover': {
      color: '#db7600'
    }
  },
  expired: {
    color: '#e74c3d',
    '&:hover': {
      color: '#c94234'
    }
  },
  proposed: {
    color: '#999999',
    '&:hover': {
      color: '#757575'
    }
  },
  active: {
    color: '#49586e',
    '&:hover': {
      color: '#3a4657'
    }
  },
  neutral: {
    color: '#3498DB',
    '&:hover': {
      color: '#2d83bd'
    }
  },
  awesome: {
    color: '#1ABC9C',
    '&:hover': {
      color: '#169c81'
    }
  },
  inDanger: {
    color: '#F48401',
    '&:hover': {
      color: '#d67300'
    }
  },
  horizontalDivider: {
    width: '100%',
    margin: '10px 0'
  }
},{ name: 'InfoPanel' });

const ObjectivesInfoPanel = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down('xs'));

  const {
    resetFilters,
    setStatusFilters,
    setDateRange,
    setDevelopmentStatusFilters
  } = useFilterContext();

  const objectivesInfoQuery = useQuery(
    [ 'objectivesInfo' ],
    async () => {
      const { data } = await fetchObjectivesInfo();

      return data;
    },
    {
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const objectivesInfo = objectivesInfoQuery.data?.[0];
  const isLoading =  objectivesInfoQuery.isFetching;

  const {
    will_expire_count,
    expired_count,
    proposed_count,
    active_count,
    awesome_count,
    neutral_count,
    in_danger_count
  } = objectivesInfo || {};

  const filterWillExpire = () => {
    resetFilters();
    setDateRange(JSON.parse(createNext2WeeksValue()));
  };

  const filterExpired = () => {
    resetFilters();
    setDateRange({
      from: convertDateToModernCalendarFormat(new Date(0)),
      to: convertDateToModernCalendarFormat(new Date())
    });
    setStatusFilters([ STATUS.PROPOSED, STATUS.DECLINED, STATUS.PASSIVE, STATUS.ACTIVE ]);
  };

  const filterStatus = status => {
    resetFilters();
    setStatusFilters(status);
  };

  const filterDevelopmentStatus = developmentStatus => {
    resetFilters();
    setStatusFilters(STATUS.ACTIVE);
    setDevelopmentStatusFilters(developmentStatus);
  };

  const createInfoItem = (count, type, onClick, isStatusType) =>
    <Grid item
      className={ `${classes[type]}
      ${classes.clickable}` }
      onClick={ onClick }
      sm='auto'
      xs={ isStatusType ? 5 : 3 }
    >
      <Grid item className={ classes.number }>
        { count || 0 }
      </Grid>
      <Grid item>
        { t(`objectivesInfo.${type}`) }
      </Grid>
    </Grid>;

  return (
    <Container>
      <Grid container className={ classes.container } justifyContent="space-around">
        {  !objectivesInfo && isLoading ?
          <CircularProgress /> :
          <>
            { createInfoItem(
              will_expire_count,
              OBJECTIVES_INFO_TYPES.WILL_EXPIRE,
              filterWillExpire,
              true
            ) }
            <Grid item>
              <Divider orientation="vertical" />
            </Grid>
            { createInfoItem(
              expired_count,
              OBJECTIVES_INFO_TYPES.EXPIRED,
              filterExpired,
              true
            ) }
            { xs ?
              <Grid item xs={ 12 }>
                <Divider
                  orientation='horizontal'
                  className={ classes.horizontalDivider }
                />
              </Grid> :
              <Grid item>
                <Divider orientation="vertical" />
              </Grid>
            }
            { createInfoItem(
              proposed_count,
              OBJECTIVES_INFO_TYPES.PROPOSED,
              () => filterStatus(STATUS.PROPOSED),
              true
            ) }
            <Grid item>
              <Divider orientation="vertical" />
            </Grid>
            { createInfoItem(
              active_count,
              OBJECTIVES_INFO_TYPES.ACTIVE,
              () => filterStatus(STATUS.ACTIVE),
              true
            ) }
            { xs ?
              <Grid item xs={ 12 }>
                <Divider
                  orientation='horizontal'
                  className={ classes.horizontalDivider }
                />
              </Grid> :
              <Grid item>
                <Divider orientation="vertical"/>
              </Grid>
            }
            { createInfoItem(
              neutral_count,
              OBJECTIVES_INFO_TYPES.NEUTRAL,
              () => filterDevelopmentStatus(DEVELOPMENT_STATUS.NEUTRAL),
              false
            )}
            <Grid item>
              <Divider orientation="vertical"/>
            </Grid>
            { createInfoItem(
              awesome_count,
              OBJECTIVES_INFO_TYPES.AWESOME,
              () => filterDevelopmentStatus(DEVELOPMENT_STATUS.AWESOME),
              false
            ) }
            <Grid item>
              <Divider orientation="vertical"/>
            </Grid>
            { createInfoItem(
              in_danger_count,
              OBJECTIVES_INFO_TYPES.IN_DANGER,
              () => filterDevelopmentStatus(DEVELOPMENT_STATUS.IN_DANGER),
              false
            )}
          </>
        }
      </Grid>
    </Container>
  );
};

export default ObjectivesInfoPanel;